import type { InvestorType } from '@workspace/api-backend'
import { Globe, Linkedin } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { InvestorTag } from '../../matches/components/investor-tag'
import {
  formatTicket,
  GEO_LABELS,
  INVESTOR_TYPE_LABELS,
  SECTOR_LABELS,
  STAGE_LABELS,
} from '../../matches/config/labels'

export interface Contact {
  id: string
  name: string
  title: string | null
  linkedinUrl: string | null
  scope: string | null
  order: number
}

export interface PortfolioCompany {
  name: string
  permalink: string | null
}

export function PortfolioList({ companies }: { companies: PortfolioCompany[] }) {
  if (companies.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {companies.map((company) =>
        company.permalink ? (
          <a
            key={company.name}
            href={`https://www.crunchbase.com/organization/${company.permalink}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md border border-border/40 bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-border/70 hover:text-foreground"
          >
            {company.name}
          </a>
        ) : (
          <span
            key={company.name}
            className="rounded-md border border-border/40 bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground"
          >
            {company.name}
          </span>
        )
      )}
    </div>
  )
}

interface ContactsGridProps {
  contacts: Contact[]
  websiteUrl: string | null
  onContact?: (contact: Contact) => void
}

export function ContactsGrid({ contacts, websiteUrl, onContact }: ContactsGridProps) {
  const [contactsExpanded, setContactsExpanded] = useState(false)
  if (contacts.length === 0) return null
  return (
    <div>
      <div className="grid grid-cols-2 items-stretch gap-2">
        {(contactsExpanded ? contacts : contacts.slice(0, 2)).map((contact) => {
          const initials = contact.name
            .split(' ')
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase()
          const isClickable = !!(contact.linkedinUrl ?? websiteUrl) && !!onContact
          const innerContent = (
            <>
              <ContactAvatar initials={initials} />
              <div className="min-w-0 flex-1 text-left">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-xs font-medium text-foreground">{contact.name}</p>
                  <Linkedin
                    className={`size-3.5 shrink-0 ${isClickable ? 'text-muted-foreground' : 'text-muted-foreground/25'}`}
                  />
                </div>
                <p className="truncate text-xs text-muted-foreground">{contact.title ?? 'GP'}</p>
                {/* Always render the scope row (with non-breaking space when
                    empty) so cards keep aligned heights when some contacts
                    have a scope and others don't. */}
                <p className="mt-0.5 text-sm text-foreground">
                  {contact.scope ?? (
                    <span className="italic text-muted-foreground/40">Focus not listed</span>
                  )}
                </p>
              </div>
            </>
          )
          const baseClass = `flex h-full items-start gap-2 rounded-lg border border-border/40 px-2.5 py-2 bg-muted/30${
            isClickable
              ? ' cursor-pointer transition-colors hover:border-border/70 hover:bg-muted/50'
              : ''
          }`
          return isClickable ? (
            <button
              key={contact.id}
              type="button"
              onClick={() => onContact?.(contact)}
              className={baseClass}
            >
              {innerContent}
            </button>
          ) : (
            <div key={contact.id} className={baseClass}>
              {innerContent}
            </div>
          )
        })}
      </div>
      {contacts.length > 2 && (
        <button
          type="button"
          onClick={() => setContactsExpanded((v) => !v)}
          className="mt-2 text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground"
        >
          {contactsExpanded ? 'Show less' : `Show ${contacts.length - 2} more partners`}
        </button>
      )}
    </div>
  )
}

interface InvestorInfoCardProps {
  name: string
  logoUrl: string | null
  description: string | null
  geographies: string[]
  sectors: string[]
  stages: string[]
  investorType: string | null
  minTicketSize: number | null
  maxTicketSize: number | null
  websiteUrl: string | null
  linkedinUrl: string | null
  recentPortfolios: { name: string; permalink: string | null }[]
  contacts: Contact[]
  onContact?: (contact: Contact) => void
}

function TagList({ tags }: { tags: string[] }) {
  if (tags.length === 0)
    return <span className="select-none text-xs text-muted-foreground/40">—</span>
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <InvestorTag key={tag}>{tag}</InvestorTag>
      ))}
    </div>
  )
}

function InvestorAvatar({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  const [imgError, setImgError] = useState(false)
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

  const resolvedLogoUrl = logoUrl?.includes('google.com/s2/favicons')
    ? logoUrl.replace(/sz=\d+/, 'sz=256')
    : logoUrl

  if (resolvedLogoUrl && !imgError) {
    return (
      <img
        src={resolvedLogoUrl}
        alt={name}
        onError={() => setImgError(true)}
        className="size-10 rounded-lg border border-border/50 bg-white/5 object-contain"
      />
    )
  }

  return (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-muted/60 text-sm font-semibold text-muted-foreground">
      {initials}
    </div>
  )
}

function ContactAvatar({ initials }: { initials: string }) {
  return (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
      {initials}
    </div>
  )
}

export function InvestorInfoCard({
  name,
  logoUrl,
  description,
  geographies,
  sectors,
  stages,
  investorType,
  minTicketSize,
  maxTicketSize,
  websiteUrl,
  linkedinUrl,
  recentPortfolios,
  contacts,
  onContact,
}: InvestorInfoCardProps) {
  const [descExpanded, setDescExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const descRef = useRef<HTMLParagraphElement>(null)
  const geoTags = geographies.map((g) => GEO_LABELS[g] ?? g)
  const stageTags = stages.map((s) => STAGE_LABELS[s] ?? s)
  const sectorTags = sectors.map((s) => SECTOR_LABELS[s] ?? s)
  const typeTags = investorType
    ? [INVESTOR_TYPE_LABELS[investorType as InvestorType] ?? investorType]
    : []
  const ticketLabel = formatTicket(minTicketSize, maxTicketSize)

  useEffect(() => {
    const el = descRef.current
    if (!el) return
    const check = () => setIsClamped(el.scrollHeight > el.clientHeight)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [description])

  return (
    <div className="rounded-xl border border-border/70 bg-card/60 px-5 py-5 shadow-sm">
      {/* Header row: avatar + name + actions */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <InvestorAvatar name={name} logoUrl={logoUrl} />
          <div>
            <h2 className="text-lg font-bold tracking-tight text-foreground">{name}</h2>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {linkedinUrl && (
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <Linkedin className="size-3.5" />
              LinkedIn
            </a>
          )}
          {websiteUrl && (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <Globe className="size-3.5" />
              Website
            </a>
          )}
        </div>
      </div>

      {/* Metadata grid — right below the header */}
      <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-5">
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Stage
          </p>
          <TagList tags={stageTags} />
        </div>
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Ticket
          </p>
          {ticketLabel ? (
            <InvestorTag>{ticketLabel}</InvestorTag>
          ) : (
            <span className="select-none text-xs text-muted-foreground/40">—</span>
          )}
        </div>
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Sector
          </p>
          <TagList tags={sectorTags} />
        </div>
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Geography
          </p>
          <TagList tags={geoTags} />
        </div>
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Type
          </p>
          <TagList tags={typeTags} />
        </div>
      </div>

      {/* Portfolio companies */}
      {recentPortfolios.length > 0 && (
        <div className="mb-4 mt-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            Portfolio
          </p>
          <PortfolioList companies={recentPortfolios} />
        </div>
      )}

      {/* Description */}
      {description && (
        <div className="mb-4">
          <p
            ref={descRef}
            className={`text-sm leading-relaxed text-muted-foreground ${!descExpanded ? 'line-clamp-3' : ''}`}
          >
            {description}
          </p>
          {(isClamped || descExpanded) && (
            <button
              type="button"
              onClick={() => setDescExpanded((v) => !v)}
              className="mt-1 text-xs text-muted-foreground/60 transition-colors hover:text-muted-foreground"
            >
              {descExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}

      {/* GP contacts list — 2-up grid */}
      <ContactsGrid contacts={contacts} websiteUrl={websiteUrl} onContact={onContact} />
    </div>
  )
}
